import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Clipboard, MessageSquareText, Send } from "lucide-react";
import { useMemo, useState } from "react";
import {
  DashboardShell,
  PageHeading,
  useAggregates,
  LoadingPanel,
} from "@/components/DashboardShell";

export const Route = createFileRoute("/feedback")({
  head: () => ({
    meta: [
      { title: "Feedback — Ready4Trade" },
      {
        name: "description",
        content: "Share suggestions and report issues about the export opportunity dashboard.",
      },
    ],
  }),
  component: FeedbackPage,
});

const CATEGORIES = [
  "General suggestion",
  "Data quality",
  "Dashboard usability",
  "New analysis request",
  "Technical issue",
] as const;

function FeedbackPage() {
  const agg = useAggregates();
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>(CATEGORIES[0]);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  const feedbackText = useMemo(
    () => `Ready4Trade feedback\nCategory: ${category}\n\n${message.trim()}`,
    [category, message],
  );

  async function copyFeedback() {
    if (!message.trim()) {
      setStatus("Please write your feedback first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(feedbackText);
      setStatus("Feedback copied. You can now send it to the dashboard owner.");
    } catch {
      setStatus("Copying was blocked by the browser. Select the text and copy it manually.");
    }
  }

  async function shareFeedback() {
    if (!message.trim()) {
      setStatus("Please write your feedback first.");
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Ready4Trade feedback",
          text: feedbackText,
        });
        setStatus("The share menu was opened.");
        return;
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
      }
    }
    await copyFeedback();
  }

  if (agg.isLoading && !agg.data)
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );

  return (
    <DashboardShell meta={agg.data?.meta}>
      <PageHeading
        title="Feedback"
        subtitle="Share suggestions, report data-quality concerns or propose new analytical features. This page cannot change the dashboard dataset."
        badge="Read-only dashboard"
      />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="surface-panel p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquareText className="w-5 h-5 text-primary" />
            <h2 className="font-display text-lg">Prepare feedback</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Choose a category and describe the issue or suggestion as clearly as possible.
          </p>

          <label
            className="block text-sm font-medium text-foreground mb-2"
            htmlFor="feedback-category"
          >
            Category
          </label>
          <select
            id="feedback-category"
            value={category}
            onChange={(event) => setCategory(event.target.value as (typeof CATEGORIES)[number])}
            className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          >
            {CATEGORIES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <label
            className="block text-sm font-medium text-foreground mt-5 mb-2"
            htmlFor="feedback-message"
          >
            Your feedback
          </label>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              setStatus(null);
            }}
            rows={9}
            placeholder="For example: The market chart would be more useful with a region filter..."
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none resize-y focus:ring-2 focus:ring-primary/30"
          />

          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              onClick={() => void shareFeedback()}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
            >
              <Send className="w-4 h-4" /> Share feedback
            </button>
            <button
              type="button"
              onClick={() => void copyFeedback()}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/70"
            >
              <Clipboard className="w-4 h-4" /> Copy feedback
            </button>
          </div>

          {status && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              {status}
            </div>
          )}
        </div>

        <aside className="surface-panel p-6">
          <h3 className="font-display text-base mb-3">Helpful feedback includes</h3>
          <ul className="text-sm text-muted-foreground space-y-3 list-disc pl-5">
            <li>The page, chart or indicator concerned.</li>
            <li>What you expected to see.</li>
            <li>What happened instead.</li>
            <li>A suggested improvement, when possible.</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-5">
            Feedback is shared through the apps available on your device or copied to your
            clipboard. It is not stored by the dashboard.
          </p>
        </aside>
      </section>
    </DashboardShell>
  );
}
